import React, { useState, useMemo } from 'react';
import { AthleteProfile, SportType } from '../../types';
import { getStateName } from '../../data/usLocations';
import { 
  MapPin, 
  Trophy, 
  TrendingUp, 
  Award, 
  Users, 
  Sparkles, 
  ChevronRight,
  ShieldCheck,
  Building2,
  Globe,
  Lock
} from 'lucide-react';

interface RegionalPerformanceCardProps {
  user: AthleteProfile;
  primarySport?: SportType;
  registeredCity?: string;
  registeredState?: string;
  onNavigateToLeaderboard?: () => void;
  onOpenProCheckout?: (msg?: string) => void;
}

export const RegionalPerformanceCard: React.FC<RegionalPerformanceCardProps> = ({
  user,
  primarySport = 'basketball',
  registeredCity,
  registeredState,
  onNavigateToLeaderboard,
  onOpenProCheckout
}) => {
  const [scope, setScope] = useState<'local' | 'state' | 'national'>('local');

  const city = registeredCity || user.registeredCity || user.location?.city || 'Venice';
  const stateCode = registeredState || user.registeredState || user.location?.state || 'CA';
  const stateName = getStateName(stateCode);

  const getSportInfo = (sport: SportType) => {
    switch (sport) {
      case 'volleyball':
        return { name: 'Volleyball', emoji: '🏐' };
      case 'soccer':
        return { name: 'Soccer', emoji: '⚽' };
      case 'pickleball':
        return { name: 'Pickleball', emoji: '🏓' };
      case 'baseball':
        return { name: 'Baseball', emoji: '⚾' };
      case 'softball':
        return { name: 'Softball', emoji: '🥎' };
      case 'football':
        return { name: 'Football', emoji: '🏈' };
      case 'tennis':
        return { name: 'Tennis', emoji: '🎾' };
      default:
        return { name: 'Basketball', emoji: '🏀' };
    }
  };

  const { name: sportName, emoji: sportEmoji } = getSportInfo(primarySport);

  const isProUser = user.isPro || user.role === 'playground_pro';

  const handleSelectScope = (targetScope: 'local' | 'state' | 'national') => {
    if (targetScope !== 'local' && !isProUser) {
      if (onOpenProCheckout) {
        onOpenProCheckout(`State and National Regional Standings are exclusive to Playground PRO athletes. Upgrade to unlock ${targetScope.toUpperCase()} leaderboards & scout visibility!`);
      }
      return;
    }
    setScope(targetScope);
  };

  // Calculate dynamic rank metrics based on user XP and stats for the active sport
  const performanceData = useMemo(() => {
    const xp = user.xp || 1200;
    const level = user.level || 5;
    const sportStat = user.stats?.[primarySport] as any;

    let basePercentile = Math.min(99, Math.max(85, Math.floor(95 - (100 / (xp / 100 + 1)))));
    if (scope === 'state') basePercentile = Math.max(80, basePercentile - 4);
    if (scope === 'national') basePercentile = Math.max(72, basePercentile - 8);

    const rankInRegion = Math.max(1, Math.floor(12 - (xp / 400)));
    const totalInRegion = scope === 'local' ? 142 : scope === 'state' ? 1280 : 15400;

    let title = 'Local Court MVP';
    if (basePercentile >= 95) title = `Top 5% ${sportName} Elite`;
    else if (basePercentile >= 90) title = `Top 10% ${sportName} Vanguard`;
    else title = `Regional ${sportName} Contender`;

    let mainStatLabel = 'Points PPG';
    let mainStatVal = '22.4';

    if (primarySport === 'volleyball') {
      mainStatLabel = 'Spike Kills / Set';
      if (sportStat?.kills && sportStat?.gamesPlayed) {
        mainStatVal = (sportStat.kills / Math.max(1, sportStat.gamesPlayed)).toFixed(1);
      } else if (sportStat?.kills) {
        mainStatVal = String(sportStat.kills);
      } else {
        mainStatVal = '4.8';
      }
    } else if (primarySport === 'soccer') {
      mainStatLabel = 'Goals / Match';
      if (sportStat?.goals && sportStat?.gamesPlayed) {
        mainStatVal = (sportStat.goals / Math.max(1, sportStat.gamesPlayed)).toFixed(1);
      } else if (sportStat?.goals) {
        mainStatVal = String(sportStat.goals);
      } else {
        mainStatVal = '1.8';
      }
    } else if (primarySport === 'pickleball') {
      mainStatLabel = 'Kitchen Dinks / Game';
      if (sportStat?.dinksCount && sportStat?.gamesPlayed) {
        mainStatVal = (sportStat.dinksCount / Math.max(1, sportStat.gamesPlayed)).toFixed(1);
      } else if (sportStat?.dinksCount) {
        mainStatVal = String(sportStat.dinksCount);
      } else {
        mainStatVal = '14.2';
      }
    } else if (primarySport === 'baseball' || primarySport === 'softball') {
      mainStatLabel = 'Batting Average';
      if (sportStat?.battingAvg) {
        mainStatVal = sportStat.battingAvg.toFixed(3).replace(/^0/, '');
      } else {
        mainStatVal = '.345';
      }
    } else if (primarySport === 'football') {
      mainStatLabel = 'Total TDs Scored';
      mainStatVal = String(sportStat?.touchdowns || 8);
    } else if (primarySport === 'tennis') {
      mainStatLabel = 'Service Aces';
      mainStatVal = String(sportStat?.aces || 12);
    } else {
      // Basketball
      if (sportStat?.pts && sportStat?.gamesPlayed) {
        mainStatVal = (sportStat.pts / Math.max(1, sportStat.gamesPlayed)).toFixed(1);
      } else {
        mainStatVal = '22.4';
      }
    }

    const totalGames = (user.winCount || 0) + (user.lossCount || 0);
    const winRate = totalGames > 0 
      ? Math.round(((user.winCount || 0) / totalGames) * 100) 
      : 75;

    return {
      percentile: basePercentile,
      rank: rankInRegion,
      totalPlayers: totalInRegion,
      title,
      mainStatLabel,
      mainStatVal,
      winRate
    };
  }, [user, primarySport, scope, sportName]);

  return (
    <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-slate-950 p-5 md:p-6 rounded-[2.5rem] border border-lime-400/30 shadow-xl space-y-4 relative overflow-hidden transition-all duration-300 hover:border-lime-400/60">
      {/* Background Glow */}
      <div className="absolute top-0 right-0 -mt-8 -mr-8 w-36 h-36 bg-lime-400/10 rounded-full blur-3xl pointer-events-none" />

      {/* Header & Regional Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-3.5">
        <div className="flex items-center space-x-2.5">
          <div className="p-2.5 bg-lime-400 text-black rounded-2xl shadow-md">
            <Trophy className="w-5 h-5 stroke-[2.5]" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-lime-400/20 text-lime-300 rounded font-mono border border-lime-400/30">
                REGIONAL STANDING
              </span>
              <span className="text-[10px] text-indigo-300 font-mono flex items-center gap-1">
                <MapPin className="w-3 h-3 text-rose-400" />
                {city}, {stateCode}
              </span>
            </div>
            <h3 className="text-lg font-black italic uppercase text-white mt-0.5 flex items-center gap-2">
              <span>{sportName} Regional Performance</span>
            </h3>
          </div>
        </div>

        {/* Local / State / National Scope Toggle Pills */}
        <div className="flex items-center bg-black/40 p-1 rounded-2xl border border-white/10 self-start sm:self-auto gap-1">
          <button
            type="button"
            onClick={() => setScope('local')}
            className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase transition flex items-center space-x-1 ${
              scope === 'local'
                ? 'bg-lime-400 text-black shadow-md'
                : 'text-indigo-200 hover:text-white'
            }`}
          >
            <MapPin className="w-3 h-3" />
            <span>Local</span>
          </button>
          
          {user.isPro ? (
            <>
              <button
                type="button"
                onClick={() => setScope('state')}
                className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase transition flex items-center space-x-1 ${
                  scope === 'state'
                    ? 'bg-lime-400 text-black shadow-md'
                    : 'text-indigo-200 hover:text-white'
                }`}
              >
                <Building2 className="w-3 h-3" />
                <span>State</span>
              </button>

              <button
                type="button"
                onClick={() => setScope('national')}
                className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase transition flex items-center space-x-1 ${
                  scope === 'national'
                    ? 'bg-lime-400 text-black shadow-md'
                    : 'text-indigo-200 hover:text-white'
                }`}
              >
                <Globe className="w-3 h-3" />
                <span>National</span>
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (onOpenProCheckout) {
                  onOpenProCheckout('State and National Regional Standings are exclusive to Playground PRO athletes. Upgrade to unlock State & National leaderboards!');
                }
              }}
              className="px-2.5 py-1 rounded-xl text-[10px] font-black uppercase transition flex items-center space-x-1 bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-400/40"
              title="Unlock State & National Standings with PRO"
            >
              <Lock className="w-3 h-3 text-amber-400" />
              <span>State & National (Unlock PRO 👑)</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Metric Banner */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Card 1: Percentile Rank Banner */}
        <div className="p-4 bg-indigo-900/60 rounded-2xl border border-white/10 flex flex-col justify-between space-y-1 relative overflow-hidden">
          <span className="text-[10px] font-extrabold uppercase text-indigo-200/80 tracking-wider">
            Regional Tier
          </span>
          <div className="flex items-baseline space-x-1">
            <span className="text-2xl font-black italic text-lime-400">
              Top {100 - performanceData.percentile}%
            </span>
          </div>
          <p className="text-[10px] font-medium text-indigo-300/80 truncate">
            {performanceData.title}
          </p>
        </div>

        {/* Card 2: Numeric Rank */}
        <div className="p-4 bg-indigo-900/60 rounded-2xl border border-white/10 flex flex-col justify-between space-y-1">
          <span className="text-[10px] font-extrabold uppercase text-indigo-200/80 tracking-wider">
            {scope === 'local' ? `${city} Rank` : scope === 'state' ? `${stateCode} Rank` : 'US Rank'}
          </span>
          <div className="flex items-baseline space-x-1">
            <span className="text-2xl font-black italic text-white">
              #{performanceData.rank}
            </span>
            <span className="text-[11px] text-indigo-300 font-mono">
              / {performanceData.totalPlayers.toLocaleString()}
            </span>
          </div>
          <p className="text-[10px] font-medium text-emerald-300 flex items-center gap-1">
            <TrendingUp className="w-3 h-3 text-emerald-400" />
            <span>+2 Spots this week</span>
          </p>
        </div>

        {/* Card 3: Sport Stat Summary */}
        <div className="p-4 bg-indigo-900/60 rounded-2xl border border-white/10 flex flex-col justify-between space-y-1">
          <span className="text-[10px] font-extrabold uppercase text-indigo-200/80 tracking-wider">
            {performanceData.mainStatLabel}
          </span>
          <div className="flex items-baseline space-x-1">
            <span className="text-2xl font-black italic text-amber-300">
              {performanceData.mainStatVal}
            </span>
          </div>
          <p className="text-[10px] font-medium text-indigo-300/80">
            {performanceData.winRate}% Win Rate in {city}
          </p>
        </div>
      </div>

      {/* Footer link to full leaderboard */}
      <div className="flex items-center justify-between pt-1">
        <span className="text-xs text-indigo-200/70 font-semibold flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-amber-400" />
          <span>Ranked among verified {sportName} players in {scope === 'local' ? `${city}, ${stateCode}` : scope === 'state' ? stateName : 'United States'}</span>
        </span>

        {onNavigateToLeaderboard && (
          <button
            type="button"
            onClick={onNavigateToLeaderboard}
            className="px-3.5 py-1.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-md transition flex items-center space-x-1 shrink-0"
          >
            <span>View Full Leaderboard</span>
            <ChevronRight className="w-4 h-4 stroke-[3]" />
          </button>
        )}
      </div>
    </div>
  );
};
